import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import ShutterSpeedIcon from '@mui/icons-material/ShutterSpeed';
import TimerIcon from '@mui/icons-material/Timer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AppDateTimePicker from 'src/common/components/form/AppDateTimePicker';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import dayjs, { toDayjs } from 'src/helpers/dayjs';
import { ECheckinTimerType } from 'src/interfaces/Checkin';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

type TimerField = 'start' | 'finish';

interface Props {
  checkinId: string;
  field: TimerField;
  startTime?: Date;
  finishTime?: Date;
  dnf?: boolean;
  onClickTimer: (id: string, type: ECheckinTimerType, timeStamp?: Date) => void;
  onSetDnf?: (id: string, dnf: boolean) => void;
}

const CheckinTimeCell = ({
  checkinId,
  field,
  startTime: st,
  finishTime: ft,
  dnf = false,
  onClickTimer,
  onSetDnf,
}: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const { dateTimeFormat } = useSystemOptionsStore(systemOptionsState => systemOptionsState);

  const [isEditing, setIsEditing] = useState(false);
  const [startTime, setStartTime] = useState<Date | undefined>(st);
  const [finishTime, setFinishTime] = useState<Date | undefined>(ft);

  const handleToggleEdit = () => {
    if (isEditing) {
      if (field === 'start' && startTime) {
        onClickTimer(checkinId, ECheckinTimerType.start, startTime);
      }
      if (field === 'finish' && finishTime) {
        onClickTimer(checkinId, ECheckinTimerType.finish, finishTime);
      }
    }
    setIsEditing(!isEditing);
  };

  const dnfButton =
    onSetDnf && st ? (
      <Tooltip title={dnf ? t('timers.clearDnf') : t('timers.setDnf')}>
        <IconButton
          aria-label={dnf ? t('timers.clearDnf') : t('timers.setDnf')}
          onClick={e => {
            e.stopPropagation();
            onSetDnf(checkinId, !dnf);
          }}
          edge="end"
          color={dnf ? 'error' : 'default'}
        >
          <BlockIcon />
        </IconButton>
      </Tooltip>
    ) : null;

  if (field === 'start') {
    if (!startTime) {
      return (
        <Tooltip title={t('timers.startTimer')}>
          <IconButton
            aria-label={t('timers.startTimer')}
            onClick={e => {
              e.stopPropagation();
              onClickTimer(checkinId, ECheckinTimerType.start);
            }}
            edge="end"
          >
            <TimerIcon />
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
        {!isEditing ? (
          dayjs(startTime).format(dateTimeFormat[currentLang])
        ) : (
          <AppDateTimePicker
            ampm={false}
            openTo="hours"
            maxDateTime={toDayjs(ft) ?? undefined}
            value={toDayjs(startTime)}
            onChange={newValue => {
              setStartTime((newValue?.toDate() as Date) || undefined);
            }}
          />
        )}
        <Tooltip title={isEditing ? t('timers.save') : t('timers.editTimer')}>
          <IconButton
            aria-label={isEditing ? t('timers.save') : t('timers.editTimer')}
            onClick={handleToggleEdit}
            edge="end"
          >
            {isEditing ? <CheckIcon sx={{ fontSize: 14 }} /> : <EditIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  if (!st || !startTime) {
    return <>-</>;
  }

  if (!finishTime) {
    return (
      <Box
        sx={{ display: 'inline-flex', alignItems: 'center' }}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <Tooltip title={t('timers.stopTimer')}>
          <IconButton
            aria-label={t('timers.stopTimer')}
            onClick={e => {
              e.stopPropagation();
              onClickTimer(checkinId, ECheckinTimerType.finish);
            }}
            edge="end"
          >
            <ShutterSpeedIcon />
          </IconButton>
        </Tooltip>
        {dnfButton}
      </Box>
    );
  }

  return (
    <div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
      {!isEditing ? (
        dayjs(finishTime).format(dateTimeFormat[currentLang])
      ) : (
        <AppDateTimePicker
          ampm={false}
          openTo="hours"
          minDateTime={toDayjs(st) ?? undefined}
          value={toDayjs(finishTime)}
          onChange={newValue => {
            setFinishTime((newValue?.toDate() as Date) || undefined);
          }}
        />
      )}
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {dnfButton}
        <Tooltip title={isEditing ? t('timers.save') : t('timers.editTimer')}>
          <IconButton
            aria-label={isEditing ? t('timers.save') : t('timers.editTimer')}
            onClick={handleToggleEdit}
            edge="end"
          >
            {isEditing ? <CheckIcon sx={{ fontSize: 14 }} /> : <EditIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
      </Box>
    </div>
  );
};

export default CheckinTimeCell;
